import { Injectable } from '@angular/core';
import { ActivityRemoteControllerService, ActorRemoteControllerService } from 'core/api';
import { ConfigService, LBatchSigning } from 'core/config';
import { ESigningOrderType, IActivityDto } from 'core/dto';
import { DataChangedSideffect, RemoteCollectionService, RemoteCollectionStore } from 'core/remotes';
import { mapKey } from 'core/rx-js';
import { LoaderService } from 'core/services';
import { ObjectUtils } from 'core/utils';
import { BehaviorSubject, Subject, Subscription, combineLatest, debounceTime, distinctUntilChanged, filter, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { DsgDeepTranslationKey } from '../../../pipes/translate.pipe';
import { IActivity } from '../dto-map/i-activity';
import { ActorHueService } from '../services/actor-hue.service';
import { SignProcessWizardService } from '../sign-process-wizard.service';
import { RemoteTaskService } from './remote-task.service';
import applyEntityLoader from './utils/apply-entity-loader';

const activityEntityLoader = applyEntityLoader('activity');

const initRemoteStore = (processId: string, activityRemoteControllerService: ActivityRemoteControllerService, loaderService: LoaderService<DsgDeepTranslationKey>, _actorRemoteControllerService: ActorRemoteControllerService) =>
  new RemoteCollectionStore<IActivityDto, string>({
    keyField: 'id',
    byKey: (uuid) => activityEntityLoader({
      obs: activityRemoteControllerService.getOne(uuid),
      loaderService,
      methodKey: 'by-key'
    }),
    load: () => activityEntityLoader({
      obs: activityRemoteControllerService.getByProcess(processId),
      loaderService,
      methodKey: 'load'
    }),
    insert: (insertDto) => activityEntityLoader({
      obs: activityRemoteControllerService.create(insertDto)
        .pipe(
          mapKey('id'),
          switchMap((activityId) => {
            if (!insertDto.actor) {
              return of(activityId);
            }

            return _actorRemoteControllerService.create({
              activityId,
              email: insertDto.actor.email,
              firstName: insertDto.actor.firstName,
              lastName: insertDto.actor.lastName,
              mobile: insertDto.actor.mobile,
            })
              .pipe(
                map(() => activityId)
              )
          }),

        )
      ,
      loaderService,
      methodKey: 'insert'
    }),
    update: (uuid, updateDto) => activityEntityLoader({
      obs: activityRemoteControllerService.update(uuid, updateDto),
      loaderService,
      methodKey: 'update',
    }),
    remove: (uuid) => activityEntityLoader({
      obs: activityRemoteControllerService.remove(uuid),
      loaderService,
      methodKey: 'remove',
      suffix: uuid
    }),
    reorderShallow: (reorderPairs) => activityEntityLoader({
      obs: activityRemoteControllerService.reorder(reorderPairs.map(({ key, orderIdx }) => ({ id: key, orderIndex: orderIdx })), processId),
      methodKey: 'reorder',
      loaderService
    })

  })


@Injectable()
export class RemoteActivityService extends RemoteCollectionService<IActivityDto, IActivity> {
  private readonly _destroy = new Subject<void>()

  readonly activityBatchSigningSubject = new BehaviorSubject<Record<string, LBatchSigning>>({})
  get #activityBatchSigning() {
    return this.activityBatchSigningSubject.value;
  }

  readonly processIdSubject = this._signProcessWizardService.processIdSubject;
  get processId() {
    return this.processIdSubject.value;
  }

  readonly activitiesSubject = this.localCollectionStore.stateSubject;
  get activities() {
    return this.activitiesSubject.value
  }

  constructor(
    private readonly _signProcessWizardService: SignProcessWizardService,
    private readonly _activityRemoteControllerService: ActivityRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _actorHueService: ActorHueService,
    private readonly _actorRemoteControllerService: ActorRemoteControllerService,
    private readonly _remoteTaskService: RemoteTaskService,
    private readonly _configService: ConfigService

  ) {
    super();

    const processId = _signProcessWizardService.processIdSubject.value;

    const activityRemoteStore = initRemoteStore(processId || '', this._activityRemoteControllerService, this._loaderService, this._actorRemoteControllerService);
    super.setConfig({
      remoteStore: activityRemoteStore,
      useLocalStore: true,
      map: (activityDto: IActivityDto) => {

        const activity = ObjectUtils.cloneDeep(activityDto) as IActivity;

        if (!activity.actor) {
          return activity;
        }

        activity.actor.hue = this._actorHueService.getHue(activity.actor?.id);
        return activity;
      },
      unMap: (activity: IActivity) => {
        delete activity.inGroupIndex;

        if (!activity?.actor) {
          return activity as IActivityDto
        }

        const actor = activity.actor
        const activityDto = <IActivityDto>ObjectUtils.cloneDeep(activity);

        activityDto.actor = {
          email: actor.email,
          firstName: actor.firstName,
          lastName: actor.lastName,
          activityId: actor.activityId,
          id: actor.id,
          mobile: actor.mobile,
          userId: actor.userId
        }

        return activityDto
      }
    })

    this.processIdSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((processId) => {

        super.remoteStore = initRemoteStore(processId || '', this._activityRemoteControllerService, _loaderService, _actorRemoteControllerService)
      })

    this.processIdSubject.pipe(
      filter(Boolean),
      distinctUntilChanged(),
      takeUntil(this._destroy)
    )
      .subscribe((processId) => {

        this.load({ autoSync: true }).subscribe()
      })


    combineLatest([this.activitiesSubject, this._remoteTaskService.tasksSubject, this.activityBatchSigningSubject]).pipe(

      filter(([activities]) => !!activities?.length),
      map(([activities, tasks, activityBatchSigning]) => {
        const activitesToCheck = activities.filter((activity) => !Object.keys(activityBatchSigning).includes(activity.id));

        if (!activitesToCheck?.length) {
          return null;
        }


        return activitesToCheck
          .map(activity => ({
            activityId: activity.id,
            tasks: tasks ? tasks.filter(task => task.activityId === activity.id) : null
          }))
          .map<{ activityId: string, batchSigning: LBatchSigning }>(({ activityId, tasks }) => {
            if (!tasks?.length) {
              return { activityId, batchSigning: this._configService.appConfig.batchSigningDefault }
            }

            const isAll = tasks.every(task => task.batchName === '*')
            if (isAll) {
              return { activityId, batchSigning: 'all' }
            }

            const isNone = tasks.every(task => !task.batchName);
            if (isNone) {
              return { activityId, batchSigning: 'none' }
            }

            return { activityId, batchSigning: 'custom' }
          })
          .reduce<Record<string, LBatchSigning>>((total, { activityId, batchSigning }) => {
            total[activityId] = batchSigning;
            return total
          },
            {}
          )

      }),
      filter(Boolean),
      takeUntil(this._destroy)
    )
      .subscribe((activityBatchSigning) => {

        this.activityBatchSigningSubject.next({
          ...this.#activityBatchSigning,
          ...activityBatchSigning
        }
        )
      })


    this.activitiesSubject
      .pipe(takeUntil(this._destroy))
      .subscribe((activites) => {
        this._signProcessWizardService.activitiesSubject.next(activites)
      })


    this.activityBatchSigningSubject.pipe(
      takeUntil(this._destroy)
    )
      .subscribe((activityBatchSigning) => {
        Object.entries(activityBatchSigning).forEach(([activityId, batchSigning]) => {
          this._remoteTaskService.updateBatchSigningByActivityId(activityId, batchSigning);
        })
      })




  }


  #signingOrderTypeSubscription: Subscription
  initializeSigningOrderTypeSubscription() {

    if (this.#signingOrderTypeSubscription) {
      return;
    }

    this.#signingOrderTypeSubscription = this._signProcessWizardService.processSubject.pipe(
      takeUntil(this._destroy),
      filter(Boolean),
      filter(() => !!this.activities.length),
      mapKey('signingOrderType'),
      debounceTime(250),
      distinctUntilChanged()
    )
      .subscribe((signingOrderType) => {

        if (signingOrderType === ESigningOrderType.paralel && this.activities.some(({ orderIndex }) => orderIndex !== 0)) {

          const reorderPairs = this.activities.map(((activity) => ({ key: activity.id, orderIdx: 0 })))
          this.reorderShallow(reorderPairs).pipe(take(1)).subscribe()
          this.activitiesSubject.next(this.activities.map(activity => ({ ...activity, orderIndex: 0 })))
        }

        if (signingOrderType === ESigningOrderType.serial && this.activities.some(({ orderIndex }, idx) => orderIndex !== idx)) {
          const reorderPairs = this.activities.map(((activity, idx) => ({ key: activity.id, orderIdx: idx })))
          this.reorderShallow(reorderPairs).pipe(take(1)).subscribe()
          this.activitiesSubject.next(this.activities.map((activity, idx) => ({ ...activity, orderIndex: idx })))
        }
      })
  }

  override remove(activityId: string, sideffect?: DataChangedSideffect) {

    const deleted = this.activities.find((activity) => activity.id === activityId);
    return super.remove(activityId, sideffect).pipe(
      tap(() => {
        this._remoteTaskService.deleteActivityTasks(activityId);
        this.#updateIndices(deleted);
      }),
    )

  }

  setActivityBatchSigning(activityId: string, batchSigning: LBatchSigning) {
    this.#activityBatchSigning[activityId] = batchSigning;
    this.activityBatchSigningSubject.next(this.#activityBatchSigning);
  }

  #updateIndices(deleted: IActivity) {

    const deletedOrderIdx = deleted.orderIndex;
    const anyDeletedIdxLeft = this.activities.some((activity) => activity.orderIndex === deletedOrderIdx)

    const groupedByGroupIdx = this.activities.reduce<Record<number, IActivity[]>>((total, current) => {
      if (!anyDeletedIdxLeft && current.orderIndex > deleted.orderIndex) {
        current.orderIndex--;
      }

      if (!total[current.orderIndex]) {
        total[current.orderIndex] = []
      }
      total[current.orderIndex].push(current)
      return total;
    },
      {}
    )

    Object.values(groupedByGroupIdx)
      .forEach((groupArr) => {
        groupArr.sort((a, b) => a.inGroupIndex - b.inGroupIndex);
        groupArr.forEach((item, idx) => {
          item.inGroupIndex = idx;
        })
      })


    const newActivities = Object.values(groupedByGroupIdx).flatMap(groupArr => groupArr);
    this.activitiesSubject.next(newActivities);

  }

  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }
}
